import { Step } from '@percent/grants-nomination-form/ui';
import { useNavigate, useParams } from 'react-router-dom';
import { NominationFormProps } from './NominationForm.types';
import { useTranslation } from 'react-i18next';
import { NominationFormPage } from './NominationFormPage/NominationFormPage';
import { useFormContext } from 'react-hook-form';
import { mapFormPayload } from './utils/mapFormPayload';
import { submitNominationFormAction } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.actions';
import { useMutation } from '@percent/grants-nomination-form/hooks';
import { useMemo } from 'react';
import { submitApplicationFormAction } from '@percent/grants-nomination-form/api/applicationForm/applicationForm.actions';
import { EntryType } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';

export const NominationForm = ({
  nominationFormData,
  pageId,
  arrayFieldsKeys,
  setNominationFormState,
}: NominationFormProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { programId } = useParams();
  const { getValues } = useFormContext();

  const formEntryType = nominationFormData.entryType;
  const shareNominatorInfoWithOrg = nominationFormData.shareNominatorInfoWithOrg;

  const { mutate: submitNominationForm, isLoading: isNominationSubmitting } = useMutation({
    mutationFn: submitNominationFormAction,
    onSuccess: () => setNominationFormState('submitted'),
    onError: () => {
      setNominationFormState('nomination-error');
      navigate(`/${programId}/submission-error`);
    },
  });

  const { mutate: submitApplicationForm, isLoading: isApplicationSubmitting } = useMutation({
    mutationFn: submitApplicationFormAction,
    onSuccess: () => setNominationFormState('submitted'),
    onError: () => {
      setNominationFormState('application-error');
      navigate(`/${programId}/submission-error`);
    },
  });

  const isLoading = isNominationSubmitting || isApplicationSubmitting;

  const currentPageIndex = nominationFormData.pages.findIndex((page) => page.id === pageId) ?? -1;
  const currentPage = nominationFormData?.pages[currentPageIndex];

  const navigateToNextPage = () => {
    const nextPageId = nominationFormData.pages[currentPageIndex + 1]?.id;

    if (currentPageIndex === -1) {
      setNominationFormState('dirty');
    }

    if (nextPageId) {
      navigate(`/${programId}/${nextPageId}`);
    } else {
      const formValues = getValues();
      const payload = mapFormPayload(arrayFieldsKeys, formValues, nominationFormData);
      const isApplication = formEntryType === EntryType.APPLICATION;
      const mutate = isApplication ? submitApplicationForm : submitNominationForm;

      mutate({ programId, data: payload, locale: i18n.language });
    }
  };

  const navigateToPreviousPage = () => {
    const previousPageId = nominationFormData.pages[currentPageIndex - 1]?.id;
    navigate(previousPageId ? `/${programId}/${previousPageId}` : `/${programId}`);
  };
  const progress = useMemo(
    () => Math.min((currentPageIndex / (nominationFormData.pages.length - 1)) * 100, 100),
    [currentPageIndex, nominationFormData.pages.length],
  );

  const formTitle = nominationFormData.title || nominationFormData.programName;

  if (!currentPage) {
    return (
      <Step
        title={formTitle}
        description={nominationFormData.description}
        descriptionAlignment="left"
        onNext={navigateToNextPage}
        nextButtonLabel={t('button.letsGo')}
      />
    );
  }

  return (
    <NominationFormPage
      page={currentPage}
      onNext={navigateToNextPage}
      onBack={navigateToPreviousPage}
      isLastPage={currentPageIndex === nominationFormData.pages.length - 1}
      isLoading={isLoading}
      progress={progress}
      shareNominatorInfoWithOrg={shareNominatorInfoWithOrg}
      allowedOrganisationsTypes={nominationFormData.allowedOrganisationsTypes}
    />
  );
};
